/**
 * Validation for contract create endpoints.
 *
 * Four contract types share the base shape and add type-specific
 * fields. We use a discriminated union so the server (and form code)
 * can refuse incompatible field combinations — e.g., monthly_hours
 * on a BAU contract. Warranty mirrors SLA but overageRateCents is
 * always 0 (hours included in build price).
 */

import { z } from 'zod';

import { centsSchema, idRefSchema, isoDateSchema, uuidSchema } from './primitives.js';

const baseContract = {
  organizationId: idRefSchema,
  name: z.string().min(1).max(200),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  accountOwnerUserId: idRefSchema,
  /** Slack webhook URL for the contract's notifications. NULL skips. */
  slackChannel: z.string().url().nullable().default(null),
  logoUrl: z.string().url().nullable().default(null),
  harvestProjectId: z.string().max(64).nullable().default(null),
};

export const slaContractCreateInputSchema = z.object({
  type: z.literal('sla'),
  ...baseContract,
  monthlyHours: z.number().int().positive().max(10_000),
  overageRateCents: centsSchema,
  responseTargetMinutes: z.number().int().positive().max(60 * 24 * 30),
  solutionTargetMinutes: z.number().int().positive().max(60 * 24 * 30),
  businessHoursOnly: z.boolean().default(true),
  pauseOutsideHours: z.boolean().default(true),
});

export const bauContractCreateInputSchema = z.object({
  type: z.literal('bau'),
  ...baseContract,
  totalHours: z.number().int().positive().max(100_000),
  durationMonths: z.number().int().positive().max(60),
  topUpsAllowed: z.boolean().default(true),
  topUpRateCents: centsSchema.nullable().default(null),
});

export const internalContractCreateInputSchema = z.object({
  type: z.literal('internal'),
  ...baseContract,
});

export const warrantyContractCreateInputSchema = z.object({
  type: z.literal('warranty'),
  ...baseContract,
  monthlyHours: z.number().int().positive().max(10_000),
  /** Always 0 for warranty — hours are included in build price. */
  overageRateCents: centsSchema.default(0),
  responseTargetMinutes: z.number().int().positive().max(60 * 24 * 30),
  solutionTargetMinutes: z.number().int().positive().max(60 * 24 * 30),
  businessHoursOnly: z.boolean().default(true),
  pauseOutsideHours: z.boolean().default(true),
});

export const contractCreateInputSchema = z.discriminatedUnion('type', [
  slaContractCreateInputSchema,
  bauContractCreateInputSchema,
  internalContractCreateInputSchema,
  warrantyContractCreateInputSchema,
]);
export type ContractCreateInput = z.infer<typeof contractCreateInputSchema>;

/**
 * Contract update — only fields that are explicitly safe to change
 * post-activation are accepted. SPEC §9.3: "Contract terms are
 * immutable after activation for audit; changes require versioning
 * (future)." So monthly_hours, response/solution targets, total_hours
 * etc are NOT editable via this endpoint. Renewals (end_date),
 * Slack channel, account owner, and lifecycle status are.
 */
export const contractUpdateInputSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  endDate: isoDateSchema.optional(),
  accountOwnerUserId: idRefSchema.optional(),
  slackChannel: z.string().url().nullable().optional(),
  logoUrl: z.string().url().nullable().optional(),
  harvestProjectId: z.string().max(64).nullable().optional(),
  status: z.enum(['active', 'expired', 'cancelled']).optional(),
});
export type ContractUpdateInput = z.infer<typeof contractUpdateInputSchema>;
